import algoliasearch from "algoliasearch/lite";
import { AnimatePresence } from "framer-motion";
import groq from "groq";
import type { GetStaticProps } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Wizard } from "../components/Wizard/Wizard";
import { WizardHeader } from "../components/WizardHeader";
import { usePreviewSubscription } from "../helpers/sanity/sanity";
import { getClient } from "../helpers/sanity/server";
import { MaxWidth } from "../layouts/MaxWidth";
import { linkUrlGroq } from "../sanity/schemas/link";
import { SchemaType } from "../sanity/types";
import { FooterProps, footerQuery } from "../sections/Footer";
import { navQuery } from "../sections/Nav";
import { WizardResults } from "../sections/WizardResults";
import { duur, niveaus, type, vakgebieden } from "../shared/filterOptions";
import { NavType } from "../types";
import { Page } from "../wrappers/Page";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || "",
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || ""
);
const index = searchClient.initIndex(
  process.env.NEXT_PUBLIC_ALGOLIA_INDEX || "Maak je Stap development"
);

const schemaType: SchemaType = "landing.wizard";

/**
 * Sanity Query
 */

const stepFields = `
  ...,
  "links": links[]{
    ...,
    "url": ${linkUrlGroq},
    "expertises": expertises[]->title
  }
`;
// const expertises = '["ICT", "Creatief"]';
const pageQuery = groq`
  {
    "page": *[_type == "${schemaType}"]{
      ...,
      "expertises": expertises{
        ${stepFields},
        "list": *[_type == "expertise"]{
          title,
          "text": tagLine,
          "image": icon,
          "url": "/vakgebieden/"+slug.current,
        }
      },
      "finder": finder{
        ${stepFields}
      },
      "financials": financials{
        ${stepFields}
      },
      "advisor": advisor{
        ${stepFields}
      },
      "knowledge": knowledge{
        ${stepFields}
      }
    }[0],
    ${footerQuery},
    ${navQuery}
  }
`;

const parseXps = (query: any) => {
  let expertises: string[] = [];
  if (typeof query.other == "string" && parseInt(query.other) > 0) {
    Object.keys(vakgebieden).forEach((vakgebied: string) => {
      expertises.push(vakgebied);
    });
    return expertises;
  }
  if (typeof query.zw == "string" && parseInt(query.zw) > 0)
    expertises.push("Zorg en Welzijn");
  if (typeof query.cr == "string" && parseInt(query.cr) > 0)
    expertises.push("Creatief");
  if (typeof query.it == "string" && parseInt(query.it) > 0)
    expertises.push("ICT");
  if (typeof query.on == "string" && parseInt(query.on) > 0)
    expertises.push("Onderwijs");
  if (typeof query.tc == "string" && parseInt(query.tc) > 0)
    expertises.push("Techniek");
  return expertises;
};

interface PageQueryType {
  page: any;
  nav: NavType;
  footer: FooterProps | {};
}

/**
 * Page
 */

interface Props {
  data: {
    post: PageQueryType;
  };
  preview: boolean;
}

const getFacetFilters = (query: any) => {
  let facetFilters = [];
  // parse expertises
  if (query.expertises) {
    let arr: any = [];
    query.expertises.forEach((expertise: any) => {
      arr.push(`vakgebied1:${expertise}`);
      arr.push(`vakgebied2:${expertise}`);
      arr.push(`vakgebied3:${expertise}`);
      arr.push(`vakgebied4:${expertise}`);
      arr.push(`vakgebied5:${expertise}`);
    }, []);
    facetFilters.push(arr);
  }
  // parse niveau
  if (typeof query.l == "string") {
    let arr;
    if (query.l === "nopref") {
      arr = niveaus.map((i) => "niveau:" + i);
    } else {
      arr = decodeURIComponent(query.l)
        .split(", ")
        .map((i) => "niveau:" + i);
    }
    facetFilters.push(arr);
  }
  // parse duur
  if (typeof query.d == "string") {
    let arr;
    if (query.d === "nopref") {
      arr = duur.map((i) => "duur:" + i);
    } else {
      arr = decodeURIComponent(query.d)
        .split(", ")
        .map((i) => "duur:" + i);
    }
    facetFilters.push(arr);
  }
  // parse type opleiding
  if (typeof query.s == "string") {
    let arr;
    if (query.s === "nopref") {
      arr = type.map((i) => "type:" + i);
    } else {
      arr = decodeURIComponent(query.s)
        .split(",")
        .map((i) => "type:" + i);
    }
    facetFilters.push(arr);
  }

  return facetFilters;
};

const SingletonPage = ({
  data = { post: { page: {}, nav: [], footer: {} } },
  preview,
}: Props) => {
  let { query } = useRouter();
  query.expertises = parseXps(query);

  const isResult = !!query.t;
  const { data: post } = usePreviewSubscription(pageQuery, {
    // params: { expertises: JSON.stringify(query.expertise) },
    params: {},
    initialData: data.post,
    enabled: preview,
  });

  const page = post.page;

  const [results, setResults] = useState([]);
  const [notFound, setNotFound] = useState<boolean>(false);

  useEffect(() => {
    if (!isResult) return;
    index
      .search("", {
        facetFilters: getFacetFilters(query),
      })
      .then((res: any) => {
        setResults(res.hits);
        if (res.hits?.length === 0) {
          setNotFound(true);
          return index.search("", {
            facetFilters: getFacetFilters({
              l: query.l,
              expertises: query.expertises,
            }),
          });
        } else {
          setNotFound(false);
        }
      })
      .then((res: any) => {
        setResults(res.hits);
      })
      .catch(() => { });
  }, [isResult]);

  return (
    <Page
      title={page.title}
      nav={post.nav}
      preview={preview}
      footer={post.footer}
    >
      <MaxWidth size="sm">
        {isResult ? (
          <>
            <WizardHeader
              title={page.title}
              intro={page.intro}
              image={page.mainImage}
            />
            <WizardResults
              {...page}
              query={query}
              results={results}
              notFound={notFound}
            />
          </>
        ) : (
          <AnimatePresence>
            <Wizard />
          </AnimatePresence>
        )}
      </MaxWidth>
    </Page>
  );
};

export default SingletonPage;

/**
 * Static props
 */

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  // const query = params || {};
  // const expertises = parseXps(query);
  const post: PageQueryType = await getClient(preview).fetch(pageQuery, {
    // expertises: JSON.stringify(expertises),
  });

  return {
    props: {
      preview,
      data: { post },
    },
    revalidate: 60,
  };
};
